import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { getApiBaseUrl } from '../config/api';
import AgentLogin from './AgentLogin';
import AgentRegistration from './AgentRegistration';
import AgentDashboard from './AgentDashboard';
import AgentResetPassword from './AgentResetPassword';
import EmployeeDashboard from './EmployeeDashboard';
import { logoutAndNavigate } from '../utils/logoutService';
import './Agent.css';

const Agent = ({ onBackToMain }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isResetPasswordPath = location.pathname.toLowerCase().includes('reset-password');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);

  useEffect(() => {
    // Check if agent or employee is already authenticated
    const checkAuth = async () => {
      const agentToken = localStorage.getItem('agentToken');
      const employeeToken = localStorage.getItem('employeeToken');
      const agentUserStr = localStorage.getItem('agentUser');
      const employeeUserStr = localStorage.getItem('employeeUser');
      const isEmployee = localStorage.getItem('isEmployee') === 'true';
      
      if (isEmployee && employeeToken && employeeUserStr) {
        // Employee is logged in
        try {
          const base = getApiBaseUrl(true);
          const url = base ? `${base}/api/employee/covers?page=1&limit=1` : '/api/employee/covers?page=1&limit=1';
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${employeeToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              const userData = JSON.parse(employeeUserStr);
              setUser({ ...userData, userType: 'employee' });
              setIsAuthenticated(true);
            } else {
              // Token invalid, clear it
              localStorage.removeItem('employeeToken');
              localStorage.removeItem('employeeUser');
              localStorage.removeItem('isEmployee');
            }
          } else {
            // Token invalid, clear it
            localStorage.removeItem('employeeToken');
            localStorage.removeItem('employeeUser');
            localStorage.removeItem('isEmployee');
          }
        } catch (error) {
          // Token invalid, clear it
          localStorage.removeItem('employeeToken');
          localStorage.removeItem('employeeUser');
          localStorage.removeItem('isEmployee');
        }
      } else if (!isEmployee && agentToken && agentUserStr) {
        // Agent is logged in
        try {
          const base = getApiBaseUrl(true);
          const url = base ? `${base}/api/agent/business?page=1&limit=1` : '/api/agent/business?page=1&limit=1';
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${agentToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setUser(JSON.parse(agentUserStr));
              setIsAuthenticated(true);
            } else {
              // Token invalid, clear it
              localStorage.removeItem('agentToken');
              localStorage.removeItem('agentUser');
            }
          } else {
            // Token invalid, clear it
            localStorage.removeItem('agentToken');
            localStorage.removeItem('agentUser');
          }
        } catch (error) {
          // Token invalid, clear it
          localStorage.removeItem('agentToken');
          localStorage.removeItem('agentUser');
        }
      }
      
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Sign-up: URL param ?action=register shows registration form
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'register' && !isAuthenticated) {
      setShowRegistration(true);
    }
  }, [searchParams, isAuthenticated]);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleRegistrationSuccess = (registrationData) => {
    // After successful registration, switch back to login
    setShowRegistration(false);
    // Registration success is handled by the registration component
  };
  
  const handleEmployeeLogout = async () => {
    setUser(null);
    setIsAuthenticated(false);
    
    await logoutAndNavigate({
      userType: 'employee',
      navigate,
      redirectPath: '/',
      showNotificationFn: null // No notification in Agent component
    });
  };

  const handleLogout = async () => {
    setUser(null);
    setIsAuthenticated(false);
    
    await logoutAndNavigate({
      userType: 'agent',
      navigate,
      redirectPath: '/',
      showNotificationFn: null // No notification in Agent component
    });
  };

  if (isLoading) {
    return (
      <div className="agent-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Loading agent portal...</p>
      </div>
    );
  }

  return (
    <div className="agent">
      {isAuthenticated ? (
        user?.userType === 'employee' ? (
          <EmployeeDashboard user={user} onLogout={handleEmployeeLogout} onBackToMain={onBackToMain} />
        ) : (
          <AgentDashboard user={user} onLogout={handleLogout} onBackToMain={onBackToMain} />
        )
      ) : isResetPasswordPath ? (
        <AgentResetPassword
          onDone={() => {
            navigate('/agent');
          }}
        />
      ) : showRegistration ? (
        <AgentRegistration 
          onRegistrationSuccess={handleRegistrationSuccess}
          onBackToLogin={() => {
            setShowRegistration(false);
            navigate('/agent');
          }}
        />
      ) : (
        <AgentLogin 
          onLogin={handleLogin} 
          onBackToMain={() => navigate('/')}
          onSwitchToRegistration={() => setShowRegistration(true)}
        />
      )}
    </div>
  );
};

export default Agent;

